import { useQuery } from "@tanstack/react-query";

import {
  getDomainOverview,
  getSiteplusAccountsMfe,
} from "@/api/onboarding";

export const siteplusAccountsMfeQueryKey = (projectsToken) => [
  "siteplusAccountsMfe",
  "primary-domain",
  projectsToken ?? "",
];

/** @param {string | null | undefined} domainName */
export const domainOverviewProdInstQueryKey = (domainName) => [
  "domainOverview",
  "prodInstId",
  domainName,
];

/**
 * Resolves the **domain** product instance id used by SFAM `/forms/:id` (link-in-bio flow):
 * projects token → Siteplus account MFE → primary `domainName` → SFcore `getDomainOverview` → `prodInstId`.
 *
 * @param {string | null | undefined} projectsToken — JWT from `getTokenForProjects` (`.token`), same as onboarding context `token`
 * @param {object} [options]
 * @param {boolean} [options.enabled=true]
 * @param {object} [options.accounts] — extra options for the accounts `useQuery`
 * @param {object} [options.overview] — extra options for the overview `useQuery`
 */
export const useDomainProductInstanceId = (projectsToken, options = {}) => {
  const {
    enabled: userEnabled = true,
    accounts: accountsQueryOptions = {},
    overview: overviewQueryOptions = {},
  } = options;

  const canRun = Boolean(projectsToken) && userEnabled;

  const accountsQuery = useQuery({
    queryKey: siteplusAccountsMfeQueryKey(projectsToken),
    queryFn: () => getSiteplusAccountsMfe(projectsToken),
    enabled: canRun,
    ...accountsQueryOptions,
  });

  const domainName = accountsQuery.data?.domain ?? null;

  const overviewQuery = useQuery({
    queryKey: domainOverviewProdInstQueryKey(domainName),
    queryFn: () => getDomainOverview(domainName),
    enabled: Boolean(domainName) && canRun,
    ...overviewQueryOptions,
  });

  const domainProductInstanceId = overviewQuery.data ?? null;

  return {
    domainProductInstanceId,
    domainName,
    accountsQuery,
    overviewQuery,
    isLoading: accountsQuery.isLoading || overviewQuery.isLoading,
    isFetching: accountsQuery.isFetching || overviewQuery.isFetching,
    isSuccess: overviewQuery.isSuccess,
    isError: accountsQuery.isError || overviewQuery.isError,
    error: accountsQuery.error ?? overviewQuery.error,
  };
};
